import React, { useState, useEffect } from 'react';
import { getActiveSupportCases, getUserContributions, getAllMembers } from '../services/firestoreService';
import ContributionModal from "../components/ContributionModal";

const Dashboard = ({ user, updateUserContributions }) => {
    const [showContributionModal, setShowContributionModal] = useState(false);
    const [selectedCase, setSelectedCase] = useState(null);
    const [activeCases, setActiveCases] = useState([]);
    const [userContributions, setUserContributions] = useState([]);
    const [communityStats, setCommunityStats] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        loadDashboardData();
    }, [user]);

    const loadDashboardData = async () => {
        try {
            setLoading(true);
            setError('');

            const [cases, contributions, members] = await Promise.all([
                getActiveSupportCases(),
                getUserContributions(user.uid),
                getAllMembers()
            ]);

            setActiveCases(cases);
            setUserContributions(contributions);

            // Calculate community statistics
            const totalMembers = members.length;
            const totalContributions = members.reduce((sum, member) => sum + (member.totalContributions || 0), 0);
            const totalAmountGiven = totalContributions * 50; // Assuming $50 per contribution
            const activeCasesCount = cases.length;

            setCommunityStats({
                totalMembers,
                activeCases: activeCasesCount,
                userContributions: contributions.length,
                totalAmountGiven
            });
        } catch (error) {
            console.error('Error loading dashboard data:', error);
            setError('Failed to load dashboard data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const stats = [
        {
            title: 'Total Members',
            value: communityStats.totalMembers?.toLocaleString() || '0',
            change: '+12 this month',
            icon: '👥',
            color: 'blue'
        },
        {
            title: 'Active Cases',
            value: communityStats.activeCases?.toString() || '0',
            change: 'Requiring support',
            icon: '🕊️',
            color: 'red'
        },
        {
            title: 'Your Contributions',
            value: communityStats.userContributions?.toString() || '0',
            change: 'All time',
            icon: '💝',
            color: 'green'
        },
        {
            title: 'Community Fund',
            value: `$${(communityStats.totalAmountGiven || 0).toLocaleString()}`,
            change: 'Total support given',
            icon: '🏦',
            color: 'purple'
        }
    ];

    const recentContributions = userContributions.slice(0, 3);

    const processContribution = async (voucherNumber, paymentMethod, caseItem, amount) => {
        // This function would be called from the ContributionModal
        // After successful contribution, reload the dashboard data
        await loadDashboardData();
    };

    if (loading) {
        return (
            <div className="space-y-8">
                {/* Welcome Section Skeleton */}
                <div className="bg-white rounded-lg shadow-sm p-6">
                    <div className="animate-pulse">
                        <div className="h-8 bg-gray-200 rounded w-1/3 mb-2"></div>
                        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                    </div>
                </div>

                {/* Stats Grid Skeleton */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {[1, 2, 3, 4].map(i => (
                        <div key={i} className="bg-white rounded-lg shadow-sm p-6">
                            <div className="animate-pulse">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
                                        <div className="h-6 bg-gray-200 rounded w-12 mb-1"></div>
                                        <div className="h-3 bg-gray-200 rounded w-16"></div>
                                    </div>
                                    <div className="w-8 h-8 bg-gray-200 rounded"></div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Active Cases Skeleton */}
                <div className="bg-white rounded-lg shadow-sm">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <div className="animate-pulse">
                            <div className="h-6 bg-gray-200 rounded w-1/4"></div>
                        </div>
                    </div>
                    <div className="p-6">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="animate-pulse border border-gray-200 rounded-lg p-6 mb-4">
                                <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                                <div className="h-3 bg-gray-200 rounded w-1/3 mb-4"></div>
                                <div className="h-2 bg-gray-200 rounded w-full mb-2"></div>
                                <div className="h-8 bg-gray-200 rounded w-full"></div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="space-y-8">
                <div className="bg-white rounded-lg shadow-sm p-6">
                    <div className="text-center py-8">
                        <div className="text-red-500 text-4xl mb-4">⚠️</div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Dashboard</h3>
                        <p className="text-gray-600 mb-4">{error}</p>
                        <button
                            onClick={loadDashboardData}
                            className="bg-primary-500 text-white px-6 py-2 rounded-lg hover:bg-primary-600 transition-colors"
                        >
                            Try Again
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-8">
            {/* Welcome Section */}
            <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex justify-between items-start">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">
                            Welcome back, {user.name}!
                        </h2>
                        <p className="text-gray-600">
                            You're part of a community of {communityStats.totalMembers?.toLocaleString() || '0'} members
                            supporting each other during difficult times.
                        </p>
                    </div>
                    <div className="text-right">
                        <p className="text-sm text-gray-600">Available Balance</p>
                        <p className="text-2xl font-bold text-green-600">${user.availableBalance || 0}</p>
                    </div>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {stats.map((stat, index) => (
                    <div key={index} className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                                <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
                                <p className="text-sm text-gray-500 mt-1">{stat.change}</p>
                            </div>
                            <span className="text-2xl">{stat.icon}</span>
                        </div>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Active Cases Section */}
                <div className="bg-white rounded-lg shadow-sm">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <div className="flex justify-between items-center">
                            <h3 className="text-lg font-semibold text-gray-900">Active Support Cases</h3>
                            <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-medium">
                {activeCases.length} cases need support
              </span>
                        </div>
                    </div>
                    <div className="p-6">
                        {activeCases.length === 0 ? (
                            <div className="text-center py-8">
                                <div className="text-gray-400 text-4xl mb-4">🕊️</div>
                                <h4 className="text-lg font-medium text-gray-900 mb-2">No Active Cases</h4>
                                <p className="text-gray-600">
                                    There are currently no active support cases requiring community assistance.
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {activeCases.slice(0, 3).map((caseItem) => (
                                    <div key={caseItem.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                                        <div className="flex justify-between items-start mb-3">
                                            <div className="flex-1">
                                                <h4 className="font-semibold text-gray-900">{caseItem.memberName || 'Community Member'}</h4>
                                                <p className="text-sm text-gray-600">Loss of {caseItem.relationship}</p>
                                                <p className="text-xs text-gray-500">
                                                    Reported {new Date(caseItem.createdAt).toLocaleDateString()}
                                                </p>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-lg font-bold text-primary-600">${caseItem.amountRaised || 0}</p>
                                                <p className="text-sm text-gray-600">of ${caseItem.amountNeeded || 1000}</p>
                                            </div>
                                        </div>

                                        {/* Progress Bar */}
                                        <div className="mb-3">
                                            <div className="flex justify-between text-xs text-gray-600 mb-1">
                                                <span>Progress</span>
                                                <span>{Math.round(((caseItem.amountRaised || 0) / (caseItem.amountNeeded || 1000)) * 100)}%</span>
                                            </div>
                                            <div className="w-full bg-gray-200 rounded-full h-2">
                                                <div
                                                    className="bg-primary-500 h-2 rounded-full transition-all duration-500"
                                                    style={{
                                                        width: `${Math.min(((caseItem.amountRaised || 0) / (caseItem.amountNeeded || 1000)) * 100, 100)}%`
                                                    }}
                                                ></div>
                                            </div>
                                        </div>

                                        <button
                                            onClick={() => {
                                                setSelectedCase(caseItem);
                                                setShowContributionModal(true);
                                            }}
                                            className="w-full bg-primary-500 text-white py-2 px-4 rounded-lg hover:bg-primary-600 transition-colors font-medium text-sm"
                                        >
                                            Contribute $50
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {activeCases.length > 0 && (
                            <button
                                onClick={() => setShowContributionModal(true)}
                                className="w-full mt-4 bg-green-500 text-white py-3 px-4 rounded-lg hover:bg-green-600 transition-colors font-medium"
                            >
                                💝 View All Cases & Contribute
                            </button>
                        )}
                    </div>
                </div>

                {/* Recent Activity Section */}
                <div className="bg-white rounded-lg shadow-sm">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900">Your Recent Activity</h3>
                    </div>
                    <div className="p-6">
                        {recentContributions.length === 0 ? (
                            <div className="text-center py-8">
                                <div className="text-gray-400 text-4xl mb-4">💝</div>
                                <h4 className="text-lg font-medium text-gray-900 mb-2">No Contributions Yet</h4>
                                <p className="text-gray-600 mb-4">
                                    You haven't made any contributions yet. Start supporting community members in need.
                                </p>
                                <button
                                    onClick={() => setShowContributionModal(true)}
                                    className="bg-primary-500 text-white px-6 py-2 rounded-lg hover:bg-primary-600 transition-colors"
                                >
                                    Make Your First Contribution
                                </button>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {recentContributions.map((contribution) => (
                                    <div key={contribution.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                                        <div className="flex items-center space-x-3">
                                            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center text-green-600">
                                                💝
                                            </div>
                                            <div>
                                                <h4 className="font-semibold text-gray-900 text-sm">
                                                    Support for {contribution.caseMemberName || 'Community Member'}
                                                </h4>
                                                <p className="text-xs text-gray-600">
                                                    {new Date(contribution.createdAt).toLocaleDateString()} •
                                                    Loss of {contribution.relationship}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <p className="font-semibold text-gray-900">${contribution.amount || 50}</p>
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Completed
                      </span>
                                        </div>
                                    </div>
                                ))}

                                {userContributions.length > 3 && (
                                    <button className="w-full text-center text-primary-600 hover:text-primary-800 font-medium py-2">
                                        View All {userContributions.length} Contributions →
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Quick Actions */}
                        <div className="mt-6 pt-6 border-t border-gray-200">
                            <h4 className="font-medium text-gray-900 mb-3">Quick Actions</h4>
                            <div className="grid grid-cols-2 gap-3">
                                <button className="bg-blue-50 text-blue-700 p-3 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium">
                                    📋 Update Profile
                                </button>
                                <button className="bg-green-50 text-green-700 p-3 rounded-lg hover:bg-green-100 transition-colors text-sm font-medium">
                                    🎫 Buy Vouchers
                                </button>
                                <button className="bg-purple-50 text-purple-700 p-3 rounded-lg hover:bg-purple-100 transition-colors text-sm font-medium">
                                    👥 View Members
                                </button>
                                <button className="bg-orange-50 text-orange-700 p-3 rounded-lg hover:bg-orange-100 transition-colors text-sm font-medium">
                                    📊 View Reports
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Community Highlights */}
            <div className="bg-white rounded-lg shadow-sm">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">Community Highlights</h3>
                </div>
                <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="text-center p-6 bg-blue-50 rounded-lg">
                            <div className="text-3xl mb-3">🤝</div>
                            <h4 className="font-semibold text-gray-900 mb-2">Strong Community</h4>
                            <p className="text-sm text-gray-600">
                                {communityStats.totalMembers?.toLocaleString() || '0'} members supporting each other
                            </p>
                        </div>
                        <div className="text-center p-6 bg-green-50 rounded-lg">
                            <div className="text-3xl mb-3">💝</div>
                            <h4 className="font-semibold text-gray-900 mb-2">Active Support</h4>
                            <p className="text-sm text-gray-600">
                                {communityStats.activeCases || 0} families receiving support
                            </p>
                        </div>
                        <div className="text-center p-6 bg-purple-50 rounded-lg">
                            <div className="text-3xl mb-3">🏆</div>
                            <h4 className="font-semibold text-gray-900 mb-2">Your Impact</h4>
                            <p className="text-sm text-gray-600">
                                {communityStats.userContributions || 0} contributions made
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Contribution Modal */}
            {showContributionModal && (
                <ContributionModal
                    activeCases={activeCases}
                    selectedCase={selectedCase}
                    setSelectedCase={setSelectedCase}
                    user={user}
                    onClose={() => {
                        setShowContributionModal(false);
                        setSelectedCase(null);
                    }}
                    onContributionComplete={loadDashboardData}
                />
            )}
        </div>
    );
};

export default Dashboard;